import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { formatDisplayDateTime } from '../../utils/dateFormatting';

import {
  useWidgetConfig,
  useWidgetStatistics,
  useWorkspaceToken
} from './hooks';
import {
  WidgetStatistics,
  WidgetSettings,
  WidgetAppearance,
  WidgetInstallation
} from './components';
import WidgetSecurity from './components/WidgetSecurity';
import WidgetPreview from './components/WidgetPreview';

const WidgetConfigForm = ({ 
  workspaceId,
  configId = null,
  onSuccess,
  onCancel,
  formData = {}
}) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Custom hooks
  const {
    widgetConfig,
    setWidgetConfig,
    loading,
    saving,
    error,
    handleConfigChange,
    toggleStatus,
    saveWidgetConfig
  } = useWidgetConfig(configId, formData);
  
  const widgetToken = useWorkspaceToken();
  
  // Initialize active section from URL or default
  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'statistics') {
      return 'overview';
    }
    if (tabParam && ['overview', 'settings', 'installation', 'security'].includes(tabParam)) {
      return tabParam;
    }
    return configId ? 'overview' : 'settings';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [previewMode, setPreviewMode] = useState('intro');

  // Initialize dateRange from URL or default to 30
  const getInitialDateRange = () => {
    const dr = Number(searchParams.get('days'));
    return dr > 0 ? dr : 30;
  };
  const [dateRange, setDateRange] = useState(getInitialDateRange());

  const handleDateRangeChange = (days) => {
    setDateRange(days);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('days', String(days));
      return next;
    });
  };

  // Sync with URL when searchParams change (e.g., browser back/forward or direct URL with param)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'statistics') {
      setActiveTab('overview');
    } else if (tabParam && ['overview', 'settings', 'installation', 'security'].includes(tabParam)) {
      setActiveTab(tabParam);
    } else {
      setActiveTab(configId ? 'overview' : 'settings');
    }
    const daysParam = Number(searchParams.get('days'));
    if (daysParam > 0 && daysParam !== dateRange) {
      setDateRange(daysParam);
    }
  }, [searchParams, configId, dateRange]);
  
  const {
    statsLoading,
    statsData
  } = useWidgetStatistics(
    loading ? null : widgetConfig.id,
    dateRange,
    activeTab === 'overview' ? 'statistics' : activeTab,
    widgetConfig.widget_linked_workspace || workspaceId
  );
  const isBehaviorOnly = activeTab === 'settings' && searchParams.get('section') === 'behavior';


  // Helper functions
  const formatDate = (dateString) => {
    return formatDisplayDateTime(dateString, i18n.language);
  };

  const getUserName = (user) => {
    if (!user) return 'Unknown user';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    if (user.email) {
      return user.email;
    }
    return 'Unknown user';
  };

  const handleSave = () => {
    saveWidgetConfig(workspaceId, onSuccess);
  };

  const getSectionHeader = () => {
    if (activeTab === 'overview') {
      return {
        title: t('widgets.config.title'),
        description: t('widgets.config.description')
      };
    }

    if (activeTab === 'settings' && isBehaviorOnly) {
      return {
        title: t('aiAgents.menu.behavior'),
        description: t('aiAgents.section.behaviorDescription')
      };
    }

    if (activeTab === 'settings') {
      return {
        title: t('aiAgents.menu.customization'),
        description: t('aiAgents.section.customizationDescription')
      };
    }

    if (activeTab === 'security') {
      return {
        title: t('aiAgents.menu.security'),
        description: t('aiAgents.section.securityDescription')
      };
    }

    if (activeTab === 'installation') {
      return {
        title: t('aiAgents.menu.installation'),
        description: t('aiAgents.section.installationDescription')
      };
    }

    return {
      title: t('widgets.config.title'),
      description: t('widgets.config.description')
    };
  };

  const sectionHeader = getSectionHeader();

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-transparent">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 py-4 pr-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">{sectionHeader.title}</h3>
              <p className="text-sm text-gray-600">
                {sectionHeader.description}
              </p>
              {activeTab === 'overview' && (
                <div className="flex items-center space-x-6 mt-4">
                  {(widgetConfig.date_updated || statsData.lastUpdated) && (
                    <div className="flex items-center text-sm text-gray-500 group relative">
                      <svg
                        className="w-4 h-4 mr-1.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        title="Last Updated"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        Last Updated
                      </div>
                      <span>
                        {formatDate(widgetConfig.date_updated || statsData.lastUpdated)}
                      </span>
                    </div>
                  )}
                  {widgetConfig.user_created && (
                    <div className="flex items-center text-sm text-gray-500">
                      <span>Created by {getUserName(widgetConfig.user_created)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Content Area */}
        <div>
            {error && (
              <div className="mb-6 mt-6 p-4 bg-red-50 text-red-700 rounded-md">
                {error}
              </div>
            )}

            {/* Section Content (controlled by left AI Agents submenu) */}
            <div className="py-6 pr-6">
              {configId && activeTab === 'overview' && (
                <WidgetStatistics
                  statsData={statsData}
                  statsLoading={statsLoading}
                  dateRange={dateRange}
                  onDateRangeChange={handleDateRangeChange}
                  widgetId={widgetConfig.id}
                />
              )}

              {activeTab === 'settings' && (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr),minmax(440px,500px)] gap-6 xl:gap-8">
                  {/* Left Column: Settings */}
                  <div className="space-y-6 min-w-0 xl:max-w-3xl">
                    <div className="space-y-6">
                      <WidgetSettings
                        widgetConfig={widgetConfig}
                        onConfigChange={handleConfigChange}
                        setWidgetConfig={setWidgetConfig}
                        onSave={handleSave}
                        saving={saving}
                        onRequestPreviewMode={setPreviewMode}
                        focusSection={searchParams.get('section')}
                        behaviorOnly={isBehaviorOnly}
                      />
                      {widgetConfig.widget_type !== 'API' && !isBehaviorOnly && (
                        <WidgetAppearance
                          widgetConfig={widgetConfig}
                          onConfigChange={handleConfigChange}
                          onSave={handleSave}
                          saving={saving}
                        />
                      )}
                    </div>
                  </div>

                  {/* Right Column: Preview or API Placeholder */}
                  <div className="hidden xl:block min-w-0">
                    {isBehaviorOnly ? null : widgetConfig.widget_type === 'API' ? (
                      <div className="sticky top-4">
                        <div className="bg-white border border-gray-200 rounded-lg p-8">
                          <div className="flex flex-col items-center justify-center text-center space-y-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">API Configuration</h3>
                              <p className="text-sm text-gray-600 mb-4">
                                This widget is configurable via API. Use our API endpoints to customize and manage your widget.
                              </p>
                              <a
                                href="https://prosecaptain.com/dokumentacja/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                View Documentation
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <WidgetPreview 
                        widgetConfig={widgetConfig}
                        externalPreviewMode={previewMode}
                        onPreviewModeChange={setPreviewMode}
                      />
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'installation' && (
                <WidgetInstallation
                  widgetConfig={widgetConfig}
                  widgetToken={widgetToken}
                />
              )}

              {activeTab === 'security' && (
                <WidgetSecurity
                  widgetConfig={widgetConfig}
                  onToggleStatus={toggleStatus}
                  onConfigChange={handleConfigChange}
                  onSave={handleSave}
                  saving={saving}
                />
              )}
            </div>
        </div>
    </div>
  );
};

export default WidgetConfigForm;
